// New team invoice, from the Invoices tab's ⋮ (`modal=new-team-invoice`, finance-real, the user 2026-10-22: "should be
// able to select the jobs here already"). The person first; then, in the same drawer, their claims on no team invoice that
// are owed (not on a team invoice yet), all ticked and each untickable (the shared JobLine: job, client and period,
// amount, its status), with what the ticked ones add up to; their claims not owed follow greyed in two groups: not owed
// yet (the job running, "Owed from <its last day>", or main's month not invoiced, "Owed once its job is invoiced"), and
// paid before team invoices (main's months its own payouts settled), so "Nothing owed to Cassie now." says why
// (the server's sum, TD-102). Below them Claim another job (the team invoice drawer's ClaimAnother, on the shared claim
// form), the job drawer's rule: the claim is made at once, on its job, and joins the list ticked. "Make the draft · <total>" makes the draft of the ticked claims and opens it in this drawer's
// place. A person with a draft has it opened instead, as Pay does: one draft each. From a person's drawer it stacks one
// level over it (`stacked`, DD-045's create flow), for that person.
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { MoneyText } from "@/components/ui/MoneyText";
import { OptionDropdown } from "@/components/ui/OptionDropdown";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { JobLine } from "../components/JobLine";
import { MissingLine, Required } from "../components/Required";
import { JOB_STATUS } from "../clientPage/lineJobText";
import { StatusPill } from "../clientPage/StatusBadge";
import type { TeamInvoiceClaim } from "../types";
import { dateLabel, prefixed } from "../utils/format";
import { closeUnlessPending } from "../utils/mutations";
import { jobSubline, NO_CLAIM } from "../utils/names";
import { useNewTeamInvoiceDrawer } from "./hooks/useNewTeamInvoiceDrawer";
import { CLAIM_ANOTHER, ClaimAnother } from "./TeamInvoiceDrawer";

const TEAM = ENTITY_CONFIG.teamInvoice;
// `contract` names the user's job now (the entityConfig shadow).
const jobWord = ENTITY_CONFIG.contract.label.toLowerCase();
const heading = "text-[11px] uppercase tracking-wide text-gray-500 mb-1";
/** The owed rule in words (the server's `rules.is_owed`). */
const OWED_RULE = `a claim becomes owed when its ${jobWord} is invoiced or its period ends`;

/** A claim as the list shows it: a claim made here reads only what the answer and its job say, no figure of its own. */
type ListedClaim = Pick<TeamInvoiceClaim, "claimId" | "jobName" | "periodLabel" | "rhythm" | "jobStatus" | "client" | "amountMinor">;

/** One group of claims not owed, greyed (text-gray-500) under its small heading: shown for why, never ticked. */
function NotOwedGroup<C extends TeamInvoiceClaim>({ title, claims, noteOf }: { title: string; claims: C[]; noteOf: (c: C) => string }) {
  if (claims.length === 0) return null;
  return (
    <div className="space-y-1">
      <h4 className="text-xs text-gray-500">{title}</h4>
      <ul className="divide-y divide-gray-100" aria-label={title}>
        {claims.map((c) => (
          <li key={c.claimId}>
            <JobLine muted name={c.jobName} sub={jobSubline(c, c.client.name)} note={noteOf(c)} amountMinor={c.amountMinor} trailing={<StatusPill look={JOB_STATUS[c.jobStatus]} />} />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface Props {
  people: { id: string; name: string }[];
  /** Picked already: from Pay, for someone owed nothing (`person=`), or from the person's drawer. */
  personId: string | null;
  /** Over the person's drawer, which has their owed claims to tick: the claim picker waits to be asked for. */
  stacked?: boolean;
  onOpened: (invoiceId: string) => void;
  onClose: () => void;
}

export function NewTeamInvoiceDrawer({ people, personId: given, stacked = false, onOpened, onClose }: Props) {
  const [personId, setPersonId] = useState<string | null>(people.some((p) => p.id === given) ? given : null);
  /** Claims made here, on another job: they join the list ticked. */
  const [made, setMade] = useState<ListedClaim[]>([]);
  /** Unticked, so a claim loaded later starts ticked. */
  const [off, setOff] = useState<string[]>([]);
  const [claiming, setClaiming] = useState(given !== null && !stacked);
  const pickPerson = (id: string) => {
    setPersonId(id);
    setMade([]);
    setOff([]);
    setClaiming(false);
  };
  const person = people.find((p) => p.id === personId);
  // Read first with nothing ticked, so the ticked list can follow what the candidates read returns.
  const [ticked, setTicked] = useState<string[]>([]);
  const { candidates, total, claim, create } = useNewTeamInvoiceDrawer(personId, ticked);
  const data = candidates.data;
  const rows: ListedClaim[] = [...(data?.openClaims ?? []), ...made.filter((m) => !(data?.openClaims ?? []).some((c) => c.claimId === m.claimId))];
  // A claim made here on a job not owed yet comes back among the not owed: it lists once, ticked, with the claims made.
  const notYetOwed = (data?.notYetOwed ?? []).filter((c) => !made.some((m) => m.claimId === c.claimId));
  const nowTicked = rows.map((r) => r.claimId).filter((id) => !off.includes(id));
  if (nowTicked.join() !== ticked.join()) setTicked(nowTicked);
  const toggle = (id: string) => setOff((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  const totalMinor = total.data ?? null;
  const busy = claim.isPending || create.isPending;
  const action = `Make the draft${ticked.length > 0 && totalMinor !== null ? ` · ${formatMoney(totalMinor, "EUR")}` : ""}`;

  const footer = !person || !data ? undefined : data.draftId ? (
    <Button onClick={() => data.draftId && onOpened(data.draftId)}>Open the draft</Button>
  ) : (
    <Button
      disabled={busy || ticked.length === 0}
      isLoading={create.isPending}
      onClick={() => create.mutateAsync({ personId: person.id, claimIds: ticked }).then((i) => onOpened(i.id), () => undefined)}
    >
      {action}
    </Button>
  );

  return (
    <FinanceDrawer title={prefixed(person?.name, TEAM.createLabel)} onClose={closeUnlessPending(busy, onClose)} footer={footer} stacked={stacked}>
      <div className="space-y-5 text-sm">
        <FormField label={<Required>Person</Required>} hint="Whose invoice to the agency it is.">
          <OptionDropdown variant="input" ariaLabel="Person" value={personId} placeholder="Pick the person" options={people.map((p) => ({ value: p.id, label: p.name }))} onChange={pickPerson} />
        </FormField>
        {person && !data && <p className="text-gray-500">{candidates.isError ? candidates.error.message : `Reading ${person.name}'s claims…`}</p>}
        {person && data && data.draftId && (
          <p className="text-gray-700">{`${person.name} has a draft ${TEAM.label.toLowerCase()} already: it opens with its claims, and takes more there. One draft each, as Pay opens it.`}</p>
        )}
        {person && data && !data.draftId && (
          <>
            <section className="space-y-2">
              <h3 className={heading}>{`${person.name}'s claims not on a ${TEAM.label.toLowerCase()} yet`}</h3>
              {rows.length === 0 ? (
                <p className="text-gray-400">{data.notYetOwed.length + data.settled.length > 0 ? `Nothing owed to ${person.name} now.` : NO_CLAIM}</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {rows.map((c) => (
                    <li key={c.claimId}>
                      <JobLine
                        name={c.jobName}
                        sub={jobSubline(c, c.client.name)}
                        amountMinor={c.amountMinor}
                        check={{ checked: !off.includes(c.claimId), onChange: () => toggle(c.claimId), disabled: busy }}
                        trailing={<StatusPill look={JOB_STATUS[c.jobStatus]} />}
                      />
                    </li>
                  ))}
                </ul>
              )}
              {/* Their claims not owed, greyed and not tickable, each group under why: so an empty or short list says what
                  is true. */}
              <NotOwedGroup title={`Not owed yet: ${OWED_RULE}`} claims={notYetOwed} noteOf={(c) => (c.owedFrom ? `Owed from ${dateLabel(c.owedFrom)}` : `Owed once its ${jobWord} is invoiced`)} />
              <NotOwedGroup title="Settled by main's payouts" claims={data.settled} noteOf={(c) => `Paid before ${TEAM.plural.toLowerCase()} · paid out ${dateLabel(c.paidOn)}`} />
              {rows.length > 0 && (
                <p className="text-gray-600">
                  {`Ticked, ${ticked.length} of ${rows.length}: `}
                  <MoneyText amountMinor={totalMinor} strong />
                </p>
              )}
            </section>
            <section className="space-y-2">
              {claiming ? (
                <ClaimAnother
                  jobs={data.claimable}
                  onClaim={(job, amountMinor) =>
                    claim.mutateAsync({ jobId: job.jobId, input: { personId: person.id, amountMinor } }).then((c) => {
                      // A claim on a job they already claim adds to that claim: its row is replaced, not repeated.
                      setMade((m) => [
                        ...m.filter((x) => x.claimId !== c.id),
                        { claimId: c.id, jobName: job.name, periodLabel: job.periodLabel, rhythm: job.rhythm, jobStatus: job.status, client: job.client, amountMinor: c.amountMinor },
                      ]);
                    })
                  }
                  pending={claim.isPending}
                  error={claim.error}
                  onReset={claim.reset}
                  onDone={() => setClaiming(false)}
                />
              ) : (
                <Button size="sm" variant="secondary" disabled={busy} data-claim-focus={CLAIM_ANOTHER} onClick={() => setClaiming(true)}>
                  {`Claim another ${jobWord}`}
                </Button>
              )}
              {create.error && <p className="text-xs text-red-600">{create.error.message}</p>}
              {/* DD-056: a disabled button says what it waits for. */}
              <MissingLine button="Make the draft" missing={ticked.length === 0 ? ["a claim, ticked or claimed"] : []} />
            </section>
          </>
        )}
      </div>
    </FinanceDrawer>
  );
}
